/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import { graphql } from 'react-relay';
import { useEffect, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type ServiceConfig, utils } from '@atlaskit/util-service-support';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useAccessRequests } from '@confluence/access-requests/entry-points/useAccessRequests.tsx';
import { usePublicLinkPageDataProvider } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { UNSAFE_expValEqualsNoExposure, expValEquals } from '@confluence/feature-experiments';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller.tsx';
import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';
import type { QueriesState } from '../Controller.tsx';

import {
	type useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data,
	type useQueriesShareAndRestrictDialogQueryVariables as useQueriesShareAndRestrictDialogQuery$variables,
} from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import {
	type useQueriesShareConfigurationQuery$data,
	type useQueriesShareConfigurationQuery$variables,
} from './__generated__/useQueriesShareConfigurationQuery.graphql.ts';
import { dataToQueriesState, dataToQueriesStateV2 } from './dataToQueriesState.ts';
import {
	type PublishModalRestrictionsInput,
	publishModalDataToQueriesState,
} from './publishModalDataToQueriesState.ts';
// eslint-disable-next-line import/no-cycle
import { usePreloadUserSuggestions } from './useUserSuggestions.ts';
// eslint-disable-next-line import/no-cycle
import { useGroupsAndTeamCountQuery } from './useGroupsCountQuery.ts';
import { useTeamData } from './useTeamData.ts';
import { useContentPermissions } from './useContentPermissions.ts';

const useQueriesShareConfigurationQuery = graphql`
	query useQueriesShareConfigurationQuery($cloudId: ID!) {
		confluence_shareConfiguration(cloudId: $cloudId) {
			disableSharingToEmails
		}
	}
`;

const useQueriesShareAndRestrictDialogQuery = gql`
	query useQueriesShareAndRestrictDialogQuery(
		$contentId: ID!
		$status: [String]
		$draftShareId: String
		$spaceKey: String!
	) {
		content(id: $contentId, status: $status, draftShareId: $draftShareId) {
			nodes {
				id
				ancestors {
					id
					hasViewRestrictions
					links {
						webui
					}
				}
				hasInheritedRestrictions
				operations {
					operation
					targetType
				}
				restrictions {
					read {
						restrictions {
							userWithRestrictions(start: 0) {
								count
								nodes {
									type
									displayName
									email
									accountId
									profilePicture {
										path
									}
									hasSpaceViewPermission
									hasSpaceEditPermission
									permissionType
									restrictingContent {
										id
										links {
											webui
											base
										}
									}
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
							group(start: 0) {
								count
								nodes {
									id
									name
									permissionType
									usageType
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
						}
					}
					update {
						restrictions {
							userWithRestrictions(start: 0) {
								count
								nodes {
									type
									displayName
									email
									accountId
									profilePicture {
										path
									}
									hasSpaceViewPermission
									hasSpaceEditPermission
									permissionType
									restrictingContent {
										id
										links {
											webui
											base
										}
									}
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
							group(start: 0) {
								count
								nodes {
									id
									name
									permissionType
									usageType
								}
								pageInfo {
									hasNextPage
									endCursor
								}
							}
						}
					}
				}
			}
		}
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			emails {
				value
				isPrimary
			}
			photos {
				value
				isPrimary
			}
		}
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
		}
	}
`;

const DEFAULT_SHARE_SERVICE_URL = '/gateway/api';

export type RefetchQueries = () => Promise<void>;

export type QueriesHookResponse =
	| {
			state: 'loading';
	  }
	| {
			state: 'error';
	  }
	| {
			state: 'loaded';
			queriesState: QueriesState;
			refetchQueries: RefetchQueries;
			/** Background refetch that doesn't trigger global loading state */
			backgroundRefetch: () => Promise<void>;
			/** Whether a background refetch is currently in progress */
			isBackgroundRefetching: boolean;
	  };

type ConfigResponse = {
	disableSharingToEmails?: boolean;
};

const useDraftShareId = createSingleQueryParamHook('draftShareId');

async function fetchConfig(cloudId: string): Promise<ConfigResponse> {
	const serviceConfig: ServiceConfig = { url: DEFAULT_SHARE_SERVICE_URL };
	const options = {
		path: `${fg('confluence_new_share_service_content_share') ? 'contentShare' : 'share'}/configuration`,
		queryParams: { cloudId },
		requestInit: { method: 'get' },
	};
	return utils.requestService(serviceConfig, options);
}

export const useQueries = ({
	contentId,
	contentType,
	publishModalRestrictions,
	spaceKey: inputSpaceKey,
	cloudId,
}: {
	contentId: string;
	contentType: ContentType;
	publishModalRestrictions?: PublishModalRestrictionsInput;
	spaceKey?: string;
	cloudId: string;
}): QueriesHookResponse => {
	const [defaultSpaceKey] = usePageSpaceKey();
	const spaceKey = inputSpaceKey || defaultSpaceKey;
	const draftShareId = useDraftShareId() ?? '';
	const { edition } = useSessionData();
	const [configData, setConfigData] = useState<ConfigResponse | null>(null);
	const [isBackgroundRefetching, setIsBackgroundRefetching] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useQueryInterop<
		useQueriesShareConfigurationQuery$data,
		useQueriesShareConfigurationQuery$variables
	>(useQueriesShareConfigurationQuery, {
		skip: !expValEquals('enable_content_share_endpoint', 'isEnabled', true),
		variables: { cloudId },
		onCompleted: (emailConfigData) => {
			setConfigData(emailConfigData.confluence_shareConfiguration || null);
		},
		onError: (configError) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'disableSharingToEmails',
					attributes: {
						message: configError?.message,
						name: configError?.name,
						stack: configError?.stack,
						cause: configError?.cause,
					},
				},
			}).fire();
		},
	});

	const {
		loading: legacyLoading,
		error,
		data,
		refetch,
	} = useQuery<
		useQueriesShareAndRestrictDialogQuery$data,
		useQueriesShareAndRestrictDialogQuery$variables
	>(useQueriesShareAndRestrictDialogQuery, {
		variables: {
			contentId,
			// In order to allow us to fetch any data for draft content always we need to specify the status
			status: ['current', 'draft', 'archived'],
			draftShareId,
			spaceKey: spaceKey || '',
		},
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const shouldUseNewQuery =
		publishModalRestrictions === undefined &&
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment');

	const {
		data: contentPermissionsData,
		loading: isContentPermissionsLoading,
		error: contentPermissionsError,
		refetch: refetchContentPermissions,
	} = useContentPermissions(
		{ cloudId, contentId },
		{
			skip: !shouldUseNewQuery,
		},
	);

	useEffect(() => {
		if (!expValEquals('enable_content_share_endpoint', 'isEnabled', true)) {
			void fetchConfig(cloudId)
				.then((result) => {
					setConfigData(result);
				})
				.catch((err) => {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
							action: 'errored',
							actionSubject: 'disableSharingToEmails',
							attributes: {
								message: err?.message,
								name: err?.name,
								stack: err?.stack,
								cause: err?.cause,
							},
						},
					}).fire();
				});
		}
	}, [cloudId, createAnalyticsEvent]);

	const publicLinkPageData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'network-only',
	});

	usePreloadUserSuggestions();

	useEffect(() => {
		if (shouldUseNewQuery && contentPermissionsError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'contentPermissionsQuery',
					attributes: {
						contentPermissionsError,
					},
				},
			}).fire();
		}

		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'shareAndRestrictDialogQuery',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
					},
				},
			}).fire();
		}
	}, [error, contentPermissionsError, createAnalyticsEvent, shouldUseNewQuery]);

	const { isGroupsCountLoading, groupAndTeamCountsMap } = useGroupsAndTeamCountQuery({
		data,
		publishModalRestrictions,
		contentPermissionsData,
		shouldUseNewQuery,
		error,
	});

	const { teamData, isTeamDataLoading } = useTeamData(
		cloudId,
		publishModalRestrictions,
		data,
		contentPermissionsData,
		shouldUseNewQuery,
	);

	const { requests: requestedUsersPendingApproval, loading: isAccessRequestsLoading } =
		useAccessRequests(
			'network-only',
			SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			publishModalRestrictions !== undefined ||
				!fg('twcg-444-invite-usd-improvements-m2-gate') ||
				!UNSAFE_expValEqualsNoExposure(
					'twcg-444-invite-usd-improvements-m2',
					'cohort',
					'variation',
				),
			'PENDING_SITE_APPROVAL',
		);

	// Skip loading state if we're doing a background refetch (allows targeted spinner control)
	const isInitialLoading =
		!isBackgroundRefetching &&
		(legacyLoading ||
			isGroupsCountLoading ||
			isTeamDataLoading ||
			isAccessRequestsLoading ||
			(shouldUseNewQuery && isContentPermissionsLoading));

	if (isInitialLoading) return { state: 'loading' };

	if (error || (shouldUseNewQuery && contentPermissionsError)) {
		// We never expect any errors here. Don't mark it as handled.
		return { state: 'error' };
	}

	// Translate the query to our internal state. If that was unsuccessful
	// (most likely because of insufficient data), consider it an error
	if (!data || (shouldUseNewQuery && !contentPermissionsData)) return { state: 'error' };

	let queriesState: QueriesState | null | undefined;

	if (!data || !edition || (shouldUseNewQuery && !contentPermissionsData)) {
		queriesState = undefined;
	} else if (publishModalRestrictions) {
		queriesState = publishModalDataToQueriesState({
			publishModalRestrictions,
			spaceKey: spaceKey || '',
			data,
			publicLinkPageData,
			contentType,
			edition,
			groupAndTeamCountsMap,
			teamData,
		});
	} else if (shouldUseNewQuery && contentPermissionsData) {
		queriesState = dataToQueriesStateV2({
			legacyData: data,
			publicLinkPageData,
			spaceKey: spaceKey || '',
			contentType,
			edition,
			configData,
			groupAndTeamCountsMap,
			teamData,
			requestedUsersPendingApproval,
			queryData: contentPermissionsData,
		});
	} else {
		queriesState = dataToQueriesState(
			data,
			publicLinkPageData,
			spaceKey || '',
			contentType,
			edition,
			configData?.disableSharingToEmails,
			groupAndTeamCountsMap,
			teamData,
			requestedUsersPendingApproval,
		);
	}

	if (!queriesState) return { state: 'error' };

	return {
		state: 'loaded',
		queriesState,
		refetchQueries: async () => {
			if (!shouldUseNewQuery) {
				await refetch();
				return;
			}

			await Promise.all([refetch(), refetchContentPermissions()]);
		},
		// Background refetch that doesn't trigger global loading state
		backgroundRefetch: async () => {
			setIsBackgroundRefetching(true);
			try {
				await Promise.all([refetch(), refetchContentPermissions()]);
			} finally {
				setIsBackgroundRefetching(false);
			}
		},
		isBackgroundRefetching,
	};
};
